import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ArrowLeft } from 'lucide-react';
import { WaxTx } from '../App';
import { getSessionKit } from '../wax/session';

const ACCOUNT_COLORS = [
  '#f59e0b','#14b8a6','#8b5cf6','#f43f5e','#10b981',
  '#3b82f6','#facc15','#ec4899','#06b6d4','#a78bfa',
  '#fb923c','#34d399','#60a5fa','#f472b6','#4ade80',
];

const TOTAL_PLOTS = 10010;
const MEDALS = ['🥇','🥈','🥉'];

const SEED_COST: Record<string,number> = {
  amaranth:10, vetiver:20, opuntia:35, moringa:50,
  jujube:70, acacia:90, mesquite:120, neem:160,
};
const FOOD_RATE: Record<string,number> = {
  amaranth:1, vetiver:2, opuntia:3, moringa:5,
  jujube:7, acacia:9, mesquite:11, neem:14,
};
const SPECIES_LIST = [
  { id:'amaranth', emoji:'🌱', name:'Amaranth' },
  { id:'vetiver',  emoji:'🌾', name:'Vetiver' },
  { id:'opuntia',  emoji:'🌵', name:'Prickly Pear' },
  { id:'moringa',  emoji:'🌿', name:'Moringa' },
  { id:'jujube',   emoji:'🫐', name:'Jujube' },
  { id:'acacia',   emoji:'🌳', name:'Acacia' },
  { id:'mesquite', emoji:'🌲', name:'Mesquite' },
  { id:'neem',     emoji:'🌴', name:'Neem' },
];

interface DbRow {
  wax_account: string;
  bhoomi_score: string;
  bhoomi_rate: string;
  food_score: string;
  food_rate: string;
  plots_count: number;
  last_updated: string;
}

const LeaderboardPage: React.FC<{ txs: WaxTx[]; onBack: ()=>void }> = ({ txs, onBack }) => {
  const [waxActor, setWaxActor] = useState('');
  const [dbRows,   setDbRows]   = useState<DbRow[]>([]);
  const [loading,  setLoading]  = useState(true);
  const restored = useRef(false);

  const fetchDb = async () => {
    try {
      const res = await fetch('/samatvam/api/samatvam/leaderboard');
      const data = await res.json();
      if (data.success) setDbRows(data.rows);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(()=>{ fetchDb(); },[]);
  useEffect(()=>{ const t=setInterval(fetchDb,60_000); return()=>clearInterval(t); },[]);

  useEffect(()=>{
    if(restored.current) return;
    restored.current=true;
    getSessionKit().restore().then(s=>{ if(s) setWaxActor(String(s.actor)); }).catch(()=>{});
  },[]);

  const acctPlots = useMemo(()=>{
    const counts:Record<string,number>={};
    const ordered=[...txs].reverse();
    let idx=0;
    ordered.forEach(tx=>{
      const n = tx.plots_count !== undefined ? tx.plots_count : Math.max(1, Math.floor(tx.amount));
      for(let k=0;k<n;k++){
        if(idx<TOTAL_PLOTS){ counts[tx.from]=(counts[tx.from]||0)+1; idx++; }
      }
    });
    return counts;
  },[txs]);

  const acctColor = useMemo(()=>{
    const ordered=[...txs].reverse();
    const order:string[]=[];
    ordered.forEach(tx=>{
      const n = tx.plots_count !== undefined ? tx.plots_count : Math.max(1, Math.floor(tx.amount));
      if(n > 0 && !order.includes(tx.from)) order.push(tx.from);
    });
    const map:Record<string,string>={};
    order.forEach((a,i)=>{ map[a]=ACCOUNT_COLORS[i%ACCOUNT_COLORS.length]; });
    return map;
  },[txs]);

  const rows = useMemo(()=>{
    const dbMap: Record<string,{bhoomScore:number;bhoomRate:number;foodScore:number;foodRate:number;plotsDb:number}> = {};
    dbRows.forEach(r=>{
      dbMap[r.wax_account]={
        bhoomScore: parseFloat(r.bhoomi_score)||0,
        bhoomRate:  parseFloat(r.bhoomi_rate)||0,
        foodScore:  parseFloat(r.food_score)||0,
        foodRate:   parseFloat(r.food_rate)||0,
        plotsDb:    r.plots_count||0,
      };
    });
    const accounts=new Set<string>([...Object.keys(acctPlots),...Object.keys(dbMap)]);
    return Array.from(accounts).map(acct=>{
      const db=dbMap[acct];
      return {
        acct,
        plots: acctPlots[acct]||0,
        bhoomScore: db?.bhoomScore||0,
        bhoomRate:  db?.bhoomRate||0,
        foodScore:  db?.foodScore||0,
        foodRate:   db?.foodRate||0,
        color: acctColor[acct]||'#475569',
      };
    }).sort((a,b)=>b.bhoomScore-a.bhoomScore||b.plots-a.plots);
  },[acctPlots,dbRows,acctColor]);

  const top3=rows.slice(0,3);
  const podiumOrder = top3.length>=2 ? [top3[1],top3[0],top3[2]].filter(Boolean) : top3;
  const podiumHeights = [160,200,130];
  const podiumMedals  = ['🥈','🥇','🥉'];

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100vh',background:'#020617',color:'white',overflow:'hidden',fontFamily:'system-ui,sans-serif'}}>

      {/* Top bar */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 20px',background:'rgba(15,23,42,0.95)',borderBottom:'1px solid rgba(255,255,255,0.07)',flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:16}}>
          <button onClick={onBack} style={{display:'flex',alignItems:'center',gap:6,color:'#94a3b8',background:'none',border:'none',cursor:'pointer',fontSize:13,fontWeight:600}}>
            <ArrowLeft size={16}/> Back to Colony
          </button>
          <div style={{width:1,height:20,background:'rgba(255,255,255,0.1)'}}/>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <div style={{width:28,height:28,background:'rgba(245,158,11,0.15)',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14}}>🏆</div>
            <span style={{fontWeight:900,fontSize:15}}>Colony Leaderboard</span>
            <span style={{fontSize:9,fontWeight:700,letterSpacing:'0.12em',textTransform:'uppercase',background:'rgba(245,158,11,0.12)',border:'1px solid rgba(245,158,11,0.25)',color:'#fcd34d',padding:'2px 8px',borderRadius:999}}>Season 1 · Phase 1</span>
          </div>
        </div>
        <div style={{fontSize:11,color:'#1e293b'}}>
          {loading?'Loading…':'Bhoomi from land · Food from trees · auto-refreshes 60s'}
        </div>
      </div>

      {/* Body */}
      <div style={{flex:1,overflowY:'auto',padding:'40px 32px'}}>
        <div style={{maxWidth:900,margin:'0 auto'}}>

          {/* Economy guide */}
          <EconomyGuide/>

          {rows.length===0 ? (
            <div style={{textAlign:'center',paddingTop:80}}>
              <div style={{fontSize:48,marginBottom:16}}>🌱</div>
              <p style={{color:'#475569',fontSize:16,fontWeight:700}}>No players yet</p>
              <p style={{color:'#334155',fontSize:13,marginTop:6,lineHeight:1.8}}>
                Own land to generate Bhoomi · Buy seeds · Plant trees to generate Food
              </p>
            </div>
          ) : (
            <>
              {/* Podium */}
              <div style={{display:'flex',justifyContent:'center',alignItems:'flex-end',gap:12,marginBottom:48}}>
                {podiumOrder.map((row,pi)=>{
                  const isMe=row.acct===waxActor;
                  const glow=pi===1?`0 0 40px ${row.color}33`:'none';
                  return (
                    <div key={row.acct} style={{display:'flex',flexDirection:'column',alignItems:'center',width:200}}>
                      <div style={{
                        width:'100%',padding:'16px 12px',textAlign:'center',borderRadius:14,
                        background:pi===1?'rgba(245,158,11,0.07)':'rgba(255,255,255,0.03)',
                        border:pi===1?'1px solid rgba(245,158,11,0.2)':'1px solid rgba(255,255,255,0.07)',
                        boxShadow:glow, marginBottom:8,
                      }}>
                        <div style={{fontSize:pi===1?36:28,marginBottom:6}}>{podiumMedals[pi]}</div>
                        <div style={{width:10,height:10,borderRadius:'50%',background:row.color,margin:'0 auto 6px',boxShadow:`0 0 6px ${row.color}`}}/>
                        <div style={{fontFamily:'monospace',fontSize:11,color:isMe?'#4ade80':'#94a3b8',fontWeight:isMe?700:400,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:'100%'}}>
                          {row.acct}{isMe?' (you)':''}
                        </div>
                        <div style={{fontSize:pi===1?26:20,fontWeight:900,color:'#d97706',margin:'8px 0 0'}}>🪙 {row.bhoomScore.toFixed(1)}</div>
                        <div style={{fontSize:10,color:'#334155',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:6}}>Bhoomi</div>
                        <div style={{fontSize:pi===1?20:16,fontWeight:700,color:'#f472b6'}}>🍎 {row.foodScore.toFixed(1)}</div>
                        <div style={{fontSize:10,color:'#334155',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:6}}>Food</div>
                        <div style={{fontSize:11,color:'#475569',marginTop:4}}>{row.plots} plot{row.plots!==1?'s':''}</div>
                      </div>
                      <div style={{
                        width:'100%',height:podiumHeights[pi]/4,borderRadius:'8px 8px 0 0',
                        background:pi===1?'linear-gradient(180deg,rgba(245,158,11,0.25),rgba(245,158,11,0.08))':'rgba(255,255,255,0.04)',
                        border:pi===1?'1px solid rgba(245,158,11,0.2)':'1px solid rgba(255,255,255,0.06)',
                        borderBottom:'none',display:'flex',alignItems:'center',justifyContent:'center',
                      }}>
                        <span style={{fontSize:pi===1?18:14,fontWeight:900,color:pi===1?'rgba(245,158,11,0.5)':'rgba(255,255,255,0.1)'}}>
                          {podiumOrder.indexOf(row)===1?1:podiumOrder.indexOf(row)===0?2:3}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Full table */}
              <div style={{background:'rgba(15,23,42,0.7)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:16,overflow:'hidden'}}>
                <div style={{display:'grid',gridTemplateColumns:'48px 1fr 70px 120px 80px 120px 80px',padding:'10px 20px',borderBottom:'1px solid rgba(255,255,255,0.07)',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.12em',color:'#334155'}}>
                  <div>#</div>
                  <div>Account</div>
                  <div style={{textAlign:'right'}}>Plots</div>
                  <div style={{textAlign:'right',color:'#d97706'}}>Bhoomi</div>
                  <div style={{textAlign:'right',color:'#92400e'}}>+/hr</div>
                  <div style={{textAlign:'right',color:'#f472b6'}}>Food</div>
                  <div style={{textAlign:'right',color:'#9d174d'}}>+/hr</div>
                </div>
                {rows.map((row,i)=>{
                  const isMe=row.acct===waxActor;
                  return (
                    <div key={row.acct}
                      style={{display:'grid',gridTemplateColumns:'48px 1fr 70px 120px 80px 120px 80px',padding:'11px 20px',borderBottom:'1px solid rgba(255,255,255,0.04)',background:isMe?'rgba(74,222,128,0.04)':'transparent',borderLeft:`2px solid ${isMe?'#4ade80':'transparent'}`,alignItems:'center',transition:'background 0.15s'}}
                      onMouseEnter={e=>{if(!isMe)(e.currentTarget as HTMLDivElement).style.background='rgba(255,255,255,0.02)';}}
                      onMouseLeave={e=>{if(!isMe)(e.currentTarget as HTMLDivElement).style.background='transparent';}}
                    >
                      <div style={{fontSize:i<3?15:12,color:i<3?'white':'#475569',fontWeight:900}}>{i<3?MEDALS[i]:i+1}</div>
                      <div style={{display:'flex',alignItems:'center',gap:8,minWidth:0}}>
                        <div style={{width:8,height:8,borderRadius:'50%',background:row.color,flexShrink:0,boxShadow:isMe?`0 0 6px ${row.color}`:'none'}}/>
                        <span style={{fontFamily:'monospace',fontSize:12,color:isMe?'#4ade80':'#94a3b8',fontWeight:isMe?700:400,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                          {row.acct}{isMe?' (you)':''}
                        </span>
                      </div>
                      <div style={{textAlign:'right',fontSize:12,color:'#f59e0b',fontWeight:700}}>{row.plots}</div>
                      <div style={{textAlign:'right',fontSize:14,color:row.bhoomScore>0?'#d97706':'#334155',fontWeight:900}}>
                        {row.bhoomScore>0?row.bhoomScore.toFixed(1):'—'}
                      </div>
                      <div style={{textAlign:'right',fontSize:11,color:row.bhoomRate>0?'#92400e':'#334155'}}>
                        {row.bhoomRate>0?`+${row.bhoomRate.toFixed(1)}`:'—'}
                      </div>
                      <div style={{textAlign:'right',fontSize:14,color:row.foodScore>0?'#f472b6':'#334155',fontWeight:900}}>
                        {row.foodScore>0?row.foodScore.toFixed(1):'—'}
                      </div>
                      <div style={{textAlign:'right',fontSize:11,color:row.foodRate>0?'#9d174d':'#334155'}}>
                        {row.foodRate>0?`+${row.foodRate.toFixed(1)}`:'—'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <p style={{textAlign:'center',fontSize:11,color:'#1e293b',marginTop:24,lineHeight:1.8}}>
            Scores saved globally · Own land to earn Bhoomi · Buy seeds and plant trees to earn Food
          </p>
        </div>
      </div>
    </div>
  );
};

function EconomyGuide() {
  const [open, setOpen] = React.useState(true);
  return (
    <div style={{marginBottom:36,border:'1px solid rgba(255,255,255,0.07)',borderRadius:16,overflow:'hidden',background:'rgba(15,23,42,0.6)'}}>
      <button onClick={()=>setOpen(o=>!o)}
        style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 20px',background:'none',border:'none',cursor:'pointer',color:'white',textAlign:'left'}}
      >
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <div style={{width:28,height:28,background:'rgba(217,119,6,0.1)',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14}}>🪙</div>
          <span style={{fontWeight:800,fontSize:14}}>How the Token Economy Works</span>
        </div>
        <span style={{fontSize:18,color:'#475569',transform:open?'rotate(180deg)':'none',transition:'transform 0.2s'}}>›</span>
      </button>
      {open&&(
        <div style={{padding:'0 20px 20px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:20}}>
            <div style={{background:'rgba(217,119,6,0.06)',border:'1px solid rgba(217,119,6,0.2)',borderRadius:12,padding:'14px'}}>
              <div style={{fontSize:22,marginBottom:6}}>🪙</div>
              <div style={{fontWeight:800,fontSize:13,color:'#d97706',marginBottom:6}}>Bhoomi</div>
              <div style={{fontSize:12,color:'#94a3b8',lineHeight:1.7}}>Every owned plot generates <span style={{color:'#fbbf24',fontWeight:700}}>0.1 Bhoomi/hr</span>. Spend Bhoomi to buy seeds from the seed shop.</div>
            </div>
            <div style={{background:'rgba(244,114,182,0.06)',border:'1px solid rgba(244,114,182,0.2)',borderRadius:12,padding:'14px'}}>
              <div style={{fontSize:22,marginBottom:6}}>🍎</div>
              <div style={{fontWeight:800,fontSize:13,color:'#f472b6',marginBottom:6}}>Food</div>
              <div style={{fontSize:12,color:'#94a3b8',lineHeight:1.7}}>Planted trees generate Food tokens per hour. Higher-tier trees produce more Food.</div>
            </div>
          </div>
          {/* Seed cost table */}
          <p style={{fontSize:10,color:'#334155',textTransform:'uppercase',letterSpacing:'0.1em',fontWeight:700,marginBottom:8}}>Seed Costs &amp; Food Output</p>
          <div style={{background:'rgba(0,0,0,0.2)',borderRadius:12,overflow:'hidden'}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',padding:'8px 14px',borderBottom:'1px solid rgba(255,255,255,0.07)',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.1em',color:'#334155'}}>
              <div>Species</div>
              <div style={{textAlign:'right',color:'#d97706'}}>Seed Cost</div>
              <div style={{textAlign:'right',color:'#f472b6'}}>Food/hr</div>
            </div>
            {SPECIES_LIST.map(sp=>(
              <div key={sp.id} style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',padding:'8px 14px',borderBottom:'1px solid rgba(255,255,255,0.04)',alignItems:'center'}}
                onMouseEnter={e=>{(e.currentTarget as HTMLDivElement).style.background='rgba(255,255,255,0.02)';}}
                onMouseLeave={e=>{(e.currentTarget as HTMLDivElement).style.background='transparent';}}
              >
                <div style={{display:'flex',alignItems:'center',gap:7}}>
                  <span style={{fontSize:15}}>{sp.emoji}</span>
                  <span style={{fontSize:12,color:'#94a3b8'}}>{sp.name}</span>
                </div>
                <div style={{textAlign:'right',fontSize:12,fontWeight:700,color:'#d97706'}}>🪙 {SEED_COST[sp.id]}</div>
                <div style={{textAlign:'right',fontSize:12,fontWeight:700,color:'#f472b6'}}>🍎 +{FOOD_RATE[sp.id]}/hr</div>
              </div>
            ))}
          </div>
          <p style={{fontSize:10,color:'#334155',marginTop:12,textAlign:'center',lineHeight:1.8}}>
            Land generates Bhoomi passively · Trees generate Food · All tokens saved globally
          </p>
        </div>
      )}
    </div>
  );
}

export default LeaderboardPage;
